import type { DialogueLine } from '../ui/Ui';
import type { ItemId, Wallet } from './Economy';

export type QuestCtx = {
  wallet: Wallet;
  flags: Set<string>;
  counters: Map<string, number>;
};

export type Step = {
  text: string;
  /** Complete by talking to this NPC (optionally only once `check` passes). */
  talk?: string;
  lines?: DialogueLine[];
  /** Said by the `talk` NPC while `check` hasn't passed yet. */
  waiting?: DialogueLine[];
  check?: (c: QuestCtx) => boolean;
  onDone?: (c: QuestCtx) => void;
  /** Where to point the tracker arrow / marker. */
  marker?: string;
};

export type Quest = {
  id: string;
  title: string;
  kind: 'main' | 'side';
  giver: string;
  offer: DialogueLine[];
  steps: Step[];
  reward: { coins: number; item?: ItemId };
  requires?: string;
};

export type QuestState = { status: 'available' | 'active' | 'done'; step: number };

const L = (speaker: string, color: string) => (...texts: string[]): DialogueLine[] => texts.map((text) => ({ speaker, color, text }));
const herald = L('Herald Bram', '#c8452f');
const captain = L('Captain Rook', '#3b5b9a');
const guard = L('Guard Dunley', '#5a6b7c');
const marigold = L('Lady Marigold', '#b0447a');
const trainer = L('Sergeant Vex', '#4f7a3a');
const barkeep = L('Moll the Barkeep', '#a5672a');
const hilda = L('Hilda Ironhand', '#8a3324');
const fennick = L('Old Fennick', '#7a6a55');
const bard = L('Lute the Bard', '#7b52b8');
const odo = L('Farmer Odo', '#9b7a2a');
const fern = L('Fern the Hunter', '#4f7a3a');
const gil = L('Gil the Fisher', '#3b6fb0');
const pim = L('Sir Pim', '#b8892f');
const brick = L('Brick the Miner', '#8a3324');

export const QUESTS: Quest[] = [
  {
    id: 'main', title: 'The Dragon of Mount Cinder', kind: 'main', giver: 'herald',
    offer: herald(
      'Make way! MAKE WAY! Lord Burnwood walks among us!',
      'My lord, the dragon under Mount Cinder has woken. Smoke on the far side of the world, every night this week.',
      'Every hero in the realm is arming up. You’ll want a real sword, and swords cost coin. The treasury is... let’s say empty.',
      'Folk all over the realm need a hand: the Bazaar, the farms, the wharf, the Keep, the mine. Earn your coin, buy a sword from Hilda, then take the Dragon Road.',
    ),
    steps: [
      { text: 'Earn coins around the realm and buy a sword at the Ironhand Forge', check: (c) => c.wallet.has('sword') || c.wallet.has('greatsword'), marker: 'smith' },
      {
        text: 'Sword in hand, find Captain Rook on the Dragon Road', talk: 'captain', marker: 'captain',
        check: (c) => c.wallet.equipped.weapon === 'sword' || c.wallet.equipped.weapon === 'greatsword',
        waiting: captain('My lord. Not without a sword in your hand. A real one.', 'Open your bag (I) and equip it, then we talk.'),
        lines: captain(
          'Lord Burnwood. So it’s true. You’re going.',
          'Follow this road past the Old Keep, all the way round the world. Mount Cinder is the black peak that glows at night.',
          'The dungeon door is at its foot. Nobody who went in has told us what’s down there.',
          'Go well, my lord. Burnwood is watching.',
        ),
      },
      { text: 'Follow the Dragon Road to Mount Cinder, on the far side of the world', check: (c) => c.flags.has('atMountain'), marker: 'mountain' },
      { text: 'Enter the dragon’s dungeon', check: (c) => c.flags.has('enteredDungeon'), marker: 'dungeon' },
    ],
    reward: { coins: 0 },
  },
  {
    id: 'purse', title: 'The Lost Purse', kind: 'side', giver: 'marigold',
    offer: marigold('Oh, disaster! My purse! Embroidered, very heavy, very mine.', 'I set it down by the stalls and it simply walked off. Find it and there’s a reward in it for you.'),
    steps: [
      { text: 'Find Marigold’s purse somewhere in the bazaar', check: (c) => c.wallet.has('purse'), marker: 'purse' },
      {
        text: 'Return the purse to Lady Marigold', talk: 'marigold', marker: 'marigold', check: (c) => c.wallet.has('purse'),
        lines: marigold('My purse! You found it! And it’s only slightly lighter. Here, take this.'),
        onDone: (c) => void c.wallet.take('purse'),
      },
    ],
    reward: { coins: 20 },
  },
  {
    id: 'guard', title: 'A Hungry Guard', kind: 'side', giver: 'guard',
    offer: guard('Twelve hours at this post and not a crumb.', 'If you’re passing Tobin’s ovens, a sweet bun would make me a very loyal friend.'),
    steps: [
      { text: 'Buy a sweet bun at Tobin’s Ovens', check: (c) => c.wallet.has('bun'), marker: 'tobin' },
      {
        text: 'Bring the bun to Guard Dunley', talk: 'guard', marker: 'guard', check: (c) => c.wallet.has('bun'),
        lines: guard('Is that... it is. Bless you. If anyone asks, I never ate on duty.'),
        onDone: (c) => void c.wallet.take('bun'),
      },
    ],
    reward: { coins: 12 },
  },
  {
    id: 'range', title: 'Target Practice', kind: 'side', giver: 'trainer',
    offer: trainer('Bought a weapon? Good. Ever swung it?', 'Hit all three practice dummies in the yard. Weapon in hand, mind you.'),
    steps: [
      { text: 'Strike the 3 practice dummies (weapon equipped)', check: (c) => (c.counters.get('dummies') ?? 0) >= 3, marker: 'dummy' },
      { text: 'Report to Sergeant Vex', talk: 'trainer', marker: 'trainer', lines: trainer('Not elegant. Not terrible. You’ll do.') },
    ],
    reward: { coins: 25 },
  },
  {
    id: 'ingots', title: 'Heavy Delivery', kind: 'side', giver: 'barkeep',
    offer: barkeep('Hilda’s ingots got delivered to my cellar door again. Every week!', 'The crate’s round the back of the tavern. Carry it to the Forge, would you? It’s heavy, so no running.'),
    steps: [
      { text: 'Pick up the crate behind the tavern', check: (c) => c.wallet.has('ingots'), marker: 'ingots' },
      {
        text: 'Deliver the ingots to Hilda at the Forge', talk: 'smith', marker: 'smith', check: (c) => c.wallet.has('ingots'),
        lines: hilda('My ingots! Moll sent them to the tavern again, didn’t she.', 'Here, for your back. And your trouble.'),
        onDone: (c) => void c.wallet.take('ingots'),
      },
    ],
    reward: { coins: 30 },
  },
  {
    id: 'thief', title: 'Stop, Thief!', kind: 'side', giver: 'fennick',
    offer: fennick('Thief! A little rat in a green hood just lifted my coin pouch!', 'He’s quick, but he can’t resist showing off. Catch him!'),
    steps: [
      { text: 'Catch the pickpocket in the green hood', check: (c) => c.flags.has('caughtThief'), marker: 'thief' },
      { text: 'Return the coins to Old Fennick', talk: 'fennick', marker: 'fennick', lines: fennick('You caught him? Ha! Keep a few of those coins. You earned them.') },
    ],
    reward: { coins: 18 },
  },
  {
    id: 'lyrics', title: 'Lost Lyrics', kind: 'side', giver: 'bard',
    offer: bard('A gust of wind took my new ballad! Four pages, all over the market.', 'Bring them back and I’ll write a verse about you. A flattering one, probably.'),
    steps: [
      { text: 'Collect 4 song sheets', check: (c) => c.wallet.count('songsheet') >= 4, marker: 'songsheet' },
      {
        text: 'Bring the pages to Lute the Bard', talk: 'bard', marker: 'bard', check: (c) => c.wallet.count('songsheet') >= 4,
        lines: bard('All four! ♪ A hero with sticky fingers, who found every page... ♪', 'Working title.'),
        onDone: (c) => void c.wallet.take('songsheet', 4),
      },
    ],
    reward: { coins: 22 },
  },
  {
    id: 'turnips', title: 'Root Trouble', kind: 'side', giver: 'odo',
    offer: odo('Three crates of turnips, my lord. Scattered across my fields by... honestly, I blame the scarecrow.', 'Fetch them back and I’ll pay you in coin, not turnips. Promise.'),
    steps: [
      { text: 'Gather 3 crates of turnips in Hollow Farms', check: (c) => c.wallet.count('turnips') >= 3, marker: 'turnips' },
      { text: 'Bring the turnips back to Farmer Odo', talk: 'odo', marker: 'odo', check: (c) => c.wallet.count('turnips') >= 3,
        lines: odo('All three! You’re a better farmer than my nephew. Here.'), onDone: (c) => void c.wallet.take('turnips', 3) },
    ],
    reward: { coins: 14 },
  },
  {
    id: 'arrows', title: 'Lost in Whisperwood', kind: 'side', giver: 'fern',
    offer: fern('I dropped my quiver chasing a boar. Deep in the trees, past the old stumps.', 'I’d go myself, but the boar is still out there. Somewhere. Watching.'),
    steps: [
      { text: 'Find Fern’s quiver deep in Whisperwood', check: (c) => c.wallet.has('lostquiver'), marker: 'lostquiver' },
      { text: 'Return the quiver to Fern', talk: 'fern', marker: 'fern', check: (c) => c.wallet.has('lostquiver'),
        lines: fern('My arrows! Every single one. If you see the boar, say nothing.'), onDone: (c) => void c.wallet.take('lostquiver') },
    ],
    reward: { coins: 12 },
  },
  {
    id: 'cider', title: 'A Thirsty Fisher', kind: 'side', giver: 'gil',
    offer: gil('Fish aren’t biting. Neither am I, until I get a mug of Tobin’s cider.', 'He’s in the Bazaar. Three coins. I’ll pay you back plus some. Probably.'),
    steps: [
      { text: 'Buy a mug of cider at Tobin’s Ovens', check: (c) => c.wallet.has('cider'), marker: 'tobin' },
      { text: 'Bring the cider to Gil at Lakeside Wharf', talk: 'gil', marker: 'gil', check: (c) => c.wallet.has('cider'),
        lines: gil('Ahh. Now THAT is a lake. Here, for your legs.'), onDone: (c) => void c.wallet.take('cider') },
    ],
    reward: { coins: 13 },
  },
  {
    id: 'banner', title: 'The Keep’s Banner', kind: 'side', giver: 'pim',
    offer: pim('A gust took the Keep’s banner clean off the wall. Last seen sailing toward Ironvein Mine.', 'It has flown over this Keep for a thousand years. Well. Mostly.'),
    steps: [
      { text: 'Find the banner near Ironvein Mine', check: (c) => c.wallet.has('banner'), marker: 'banner' },
      { text: 'Return the banner to Sir Pim at the Old Keep', talk: 'pim', marker: 'pim', check: (c) => c.wallet.has('banner'),
        lines: pim('Our colours! I shall hang them myself. With nails, this time.'), onDone: (c) => void c.wallet.take('banner') },
    ],
    reward: { coins: 18 },
  },
  {
    id: 'lantern', title: 'Into the Dark', kind: 'side', giver: 'brick',
    offer: brick('Dropped my lantern by the old shaft. Can’t go back for it. Something down there hums.', 'You look like you don’t mind humming.'),
    steps: [
      { text: 'Recover the lantern by the old shaft at Ironvein Mine', check: (c) => c.wallet.has('lantern'), marker: 'lantern' },
      { text: 'Give the lantern back to Brick', talk: 'brick', marker: 'brick', check: (c) => c.wallet.has('lantern'),
        lines: brick('Still warm. Like I said. Take this, and don’t tell me what hums.'), onDone: (c) => void c.wallet.take('lantern') },
    ],
    reward: { coins: 20 },
  },
];

export type QuestEvent = { type: 'started' | 'step' | 'completed'; quest: Quest; step?: Step };

export class QuestLog {
  readonly state = new Map<string, QuestState>();
  onEvent: (e: QuestEvent) => void = () => {};

  constructor(private readonly ctx: QuestCtx) {
    for (const q of QUESTS) this.state.set(q.id, { status: 'available', step: 0 });
  }

  get(id: string): QuestState {
    return this.state.get(id)!;
  }

  active(): Quest[] {
    return QUESTS.filter((q) => this.get(q.id).status === 'active').sort((a, b) => (a.kind === 'main' ? -1 : b.kind === 'main' ? 1 : 0));
  }

  currentStep(q: Quest): Step | null {
    const s = this.get(q.id);
    return s.status === 'active' ? q.steps[s.step] : null;
  }

  /** Called every frame: auto-complete steps whose condition is met. */
  update(): void {
    for (const q of QUESTS) {
      const s = this.get(q.id);
      if (s.status !== 'active') continue;
      const step = q.steps[s.step];
      if (!step.talk && step.check?.(this.ctx)) this.advance(q);
    }
  }

  /** What an NPC says right now and what happens when the conversation ends. */
  conversation(npcId: string): { lines: DialogueLine[]; onClose: () => void } | null {
    for (const q of QUESTS) {
      const step = this.currentStep(q);
      if (step?.talk !== npcId) continue;
      const ready = step.check ? step.check(this.ctx) : true;
      if (!ready) return step.waiting ? { lines: step.waiting, onClose: () => {} } : null;
      return {
        lines: step.lines ?? [],
        onClose: () => {
          step.onDone?.(this.ctx);
          this.advance(q);
        },
      };
    }
    for (const q of QUESTS) {
      const s = this.get(q.id);
      if (q.giver !== npcId || s.status !== 'available') continue;
      if (q.requires && this.get(q.requires).status !== 'done') continue;
      return { lines: q.offer, onClose: () => this.start(q) };
    }
    return null;
  }

  /** '!' = has a quest for you, '?' = waiting for you to come back. */
  markerFor(npcId: string): '!' | '?' | null {
    for (const q of QUESTS) {
      const step = this.currentStep(q);
      if (step?.talk === npcId && (!step.check || step.check(this.ctx))) return '?';
    }
    for (const q of QUESTS) {
      if (q.giver === npcId && this.get(q.id).status === 'available' && (!q.requires || this.get(q.requires).status === 'done')) return '!';
    }
    return null;
  }

  isTarget(key: string): boolean {
    return this.active().some((q) => this.currentStep(q)?.marker === key);
  }

  private start(q: Quest): void {
    this.get(q.id).status = 'active';
    this.onEvent({ type: 'started', quest: q });
  }

  private advance(q: Quest): void {
    const s = this.get(q.id);
    s.step += 1;
    if (s.step >= q.steps.length) {
      s.status = 'done';
      this.ctx.wallet.earn(q.reward.coins);
      if (q.reward.item) this.ctx.wallet.give(q.reward.item);
      this.onEvent({ type: 'completed', quest: q });
    } else {
      this.onEvent({ type: 'step', quest: q, step: q.steps[s.step] });
    }
  }
}
